/**
 * editor-next Frame Canvas 展示组件
 *
 * 用于 designer-editor 的只读缩略图与预览场景，不依赖旧 DOM Preview 扩展。
 */
import React, { useEffect, useRef } from 'react';
import { editor } from '@/instance';

type FrameCanvasProps = React.CanvasHTMLAttributes<HTMLCanvasElement> & {
    frameId: number;
};

/**
 * 将指定 Frame 的 Canvas 同步到独立预览画布
 *
 * @param props - Canvas 属性与目标 Frame ID
 * @returns Frame 预览画布
 */
const FrameCanvas: React.FC<FrameCanvasProps> = ({ frameId, style, ...canvasProps }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const syncCanvas = (source: HTMLCanvasElement) => {
            const context = canvas.getContext('2d');
            if (!context || source.width <= 10 || source.height <= 10) return;
            canvas.width = source.width;
            canvas.height = source.height;
            context.clearRect(0, 0, canvas.width, canvas.height);
            context.drawImage(source, 0, 0);
        };

        const frame = editor.services.frame.get(frameId);
        if (frame) syncCanvas(frame.canvas);
        const previewHandler = editor.services.frame.onPreviewComplete(
            ({ frameId: completedFrameId, canvas: source }) => {
                if (completedFrameId === frameId) syncCanvas(source);
            },
        );

        return () => editor.services.events.off(previewHandler);
    }, [frameId]);

    return (
        <canvas
            {...canvasProps}
            ref={canvasRef}
            style={{ display: 'block', width: '100%', height: '100%', ...style }}
        />
    );
};

export default React.memo(FrameCanvas);
